import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import theme from '../styles/theme';

// Create context
const MobileContext = createContext();

// Custom hook to use the mobile context
export const useMobile = () => {
  const context = useContext(MobileContext);
  if (!context) {
    throw new Error('useMobile must be used within a MobileProvider');
  }
  return context;
};

// Provider component
export const MobileProvider = ({ children }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [expandedPanels, setExpandedPanels] = useState({
    gameStats: true,
    patternEditor: true
  });

  // Detect mobile devices and handle window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= parseInt(theme.breakpoints.sm, 10));
    };
    
    // Check on initial load
    checkMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Function to toggle panel expansion - memoized to avoid recreating on every render
  const togglePanel = useCallback((panelName, isExpanded) => {
    setExpandedPanels(prev => {
      // Only update if the value has changed
      if (prev[panelName] === isExpanded) return prev;
      return {
        ...prev,
        [panelName]: isExpanded
      };
    });
  }, []);

  // Calculate panel positions based on which ones are expanded - memoized
  const calculatePanelPosition = useCallback((panelName) => {
    if (!isMobile) return { order: 1, bottom: 'auto', zIndex: 1000 };

    // Default stacking order
    const orderMap = {
      gameStats: 1,  // Bottom
      patternEditor: 2  // Top
    };

    const order = orderMap[panelName];
    
    // Base height for collapsed and expanded panels
    const collapsedHeight = 50;  // Height of header only
    const expandedHeight = 300;  // Approximate height when expanded
    
    // Calculate bottom position
    let bottom = 0;
    
    // Add height of panels below this one
    Object.entries(orderMap).forEach(([panel, panelOrder]) => {
      if (panelOrder < order) {
        bottom += expandedPanels[panel] ? expandedHeight : collapsedHeight;
      }
    });
    
    return { 
      order, 
      bottom: `${bottom}px`,
      zIndex: 1000 + order
    };
  }, [isMobile, expandedPanels]);

  // Memoize the context value to prevent unnecessary renders
  const value = useMemo(() => ({
    isMobile,
    expandedPanels,
    togglePanel,
    calculatePanelPosition
  }), [isMobile, expandedPanels, togglePanel, calculatePanelPosition]);

  return <MobileContext.Provider value={value}>{children}</MobileContext.Provider>;
};

export default MobileContext; 